import { touchResources } from './resources';

// The videos the editor paints (contract 2.13.0). The stage plays a page's clips with one
// <video> per clip (`clipPlayer`): the frame it shows, playing or seeked to the playhead, is
// what the stage paints. Every other surface (thumbnails, the chat cards, a PNG of the page)
// paints a still, the frame at one second of the file, grabbed once and kept (`readyStill`).
// The MP4 export decodes the files itself (exportVideo). Remote videos load with CORS so the
// canvas stays exportable; whatever becomes ready repaints every surface.
const hasDom = () => typeof document !== 'undefined' && typeof document.createElement === 'function';

function createVideo(src) {
  const video = document.createElement('video');
  if (/^https?:/i.test(src)) video.crossOrigin = 'anonymous';
  video.muted = true;
  video.playsInline = true;
  video.preload = 'auto';
  video.src = src;
  return video;
}

// ── the stage's players, one per clip ──
// A player with a new frame (loaded, seeked) tells the playbacks listening, not every
// surface: thumbnails paint stills, and effect renders stay kept.
const players = new Map();
const playerListeners = new Set();
export const onPlayerFrame = listener => { playerListeners.add(listener); return () => playerListeners.delete(listener); };
const framed = () => playerListeners.forEach(listener => listener());
export function clipPlayer(layer) {
  const src = layer?.source?.url;
  if (!src || !hasDom()) return null;
  let entry = players.get(layer.id);
  if (!entry || entry.src !== src) {
    if (entry) stop(entry.video);
    const video = createVideo(src);
    ['loadeddata', 'seeked'].forEach(name => video.addEventListener(name, framed));
    entry = { src, video };
    players.set(layer.id, entry);
  }
  return entry.video;
}
// A player shows a frame once it has one and is not between two.
export const playerReady = video => !!video && video.readyState >= 2 && !video.seeking && video.videoWidth > 0;
function stop(video) {
  video.pause();
  video.removeAttribute('src');
  video.load();
}
// Lets go of the players of clips no longer on the stage.
export function releasePlayers(keep = []) {
  const wanted = new Set(keep);
  players.forEach((entry, id) => { if (!wanted.has(id)) { stop(entry.video); players.delete(id); } });
}

// ── stills: a frame of a file, kept ──
const stills = new Map();
const grabbers = new Map();
// Kept by pixels, not count: hundreds of filmstrip tiles, a handful of full frames (~120 MB).
const STILL_PIXELS = 30e6;
let stillPixels = 0;
const keepStill = (key, entry) => {
  stills.set(key, entry);
  for (const [oldKey, old] of stills) {
    if (stillPixels <= STILL_PIXELS || oldKey === key) break;
    if (old.status === 'loading') continue;
    stillPixels -= old.canvas ? old.canvas.width * old.canvas.height : 0;
    stills.delete(oldKey);
  }
};
const SEEK_TIMEOUT_MS = 12000;

const once = (target, names, timeout = SEEK_TIMEOUT_MS) => new Promise((resolve, reject) => {
  const done = event => { clear(); (event.type === 'error' ? reject : resolve)(event); };
  const timer = setTimeout(() => { clear(); reject(new Error('timeout')); }, timeout);
  const clear = () => { clearTimeout(timer); names.forEach(name => target.removeEventListener(name, done)); };
  names.forEach(name => target.addEventListener(name, done));
});

// One hidden video per file grabs its frames, one seek after the other.
function grabberFor(src) {
  let grabber = grabbers.get(src);
  if (!grabber) {
    const video = createVideo(src);
    grabber = { video, chain: video.readyState >= 1 ? Promise.resolve() : once(video, ['loadedmetadata', 'error']).catch(() => null) };
    grabbers.set(src, grabber);
  }
  return grabber;
}

async function grab(src, time, maxSide) {
  const grabber = grabberFor(src);
  const { video } = grabber;
  if (video.error || !video.videoWidth) throw new Error('video');
  const target = Math.max(0, Math.min(time, (video.duration || time) - 0.02));
  if (Math.abs(video.currentTime - target) > 0.001 || video.readyState < 2) {
    const seeked = once(video, ['seeked', 'error']);
    video.currentTime = target;
    await seeked;
  }
  const scale = Math.min(1, maxSide / Math.max(video.videoWidth, video.videoHeight));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
  canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
  canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
  return canvas;
}

// The frame at `time` (seconds in the file), at most `maxSide` pixels: `{ status, canvas, promise }`.
export function requestStill(src, time = 0, { maxSide = 1920 } = {}) {
  if (!src || !hasDom()) return { status: 'error', canvas: null, promise: Promise.resolve(null) };
  const key = `${src}|${Math.round(time * 100) / 100}|${maxSide}`;
  const known = stills.get(key);
  if (known) return known;
  const entry = { status: 'loading', canvas: null, promise: null };
  const grabber = grabberFor(src);
  entry.promise = grabber.chain = grabber.chain.then(() => grab(src, time, maxSide)).then(canvas => {
    entry.status = 'ready';
    entry.canvas = canvas;
    stillPixels += canvas.width * canvas.height;
    keepStill(key, entry);
    touchResources();
    return canvas;
  }, () => {
    entry.status = 'error';
    touchResources();
    return null;
  });
  keepStill(key, entry);
  return entry;
}
export const readyStill = (src, time, options) => { const entry = requestStill(src, time, options); return entry.status === 'ready' ? entry.canvas : null; };

// `count` frames spread over the file (a timeline's filmstrip), each `maxSide` pixels at most.
export const filmstrip = (src, duration, count, { from = 0, to = duration, maxSide = 160 } = {}) => Promise.all(Array.from({ length: count },
  (_, index) => requestStill(src, from + ((to - from) * (index + 0.5)) / count, { maxSide }).promise));

// The same frames painted side by side on `canvas` (each tile cropped to fill), each one as
// soon as it is grabbed. Answers a function that stops painting (a newer strip took over).
export function paintFilmstrip(canvas, src, { from, to, count, maxSide = 160 }) {
  let alive = true;
  const dpr = Math.min(2, (typeof window !== 'undefined' && window.devicePixelRatio) || 1);
  canvas.width = Math.max(1, Math.round(canvas.clientWidth * dpr));
  canvas.height = Math.max(1, Math.round(canvas.clientHeight * dpr));
  const ctx = canvas.getContext('2d');
  const step = canvas.width / count;
  Array.from({ length: count }, (_, index) => index).forEach(index => {
    requestStill(src, from + ((to - from) * (index + 0.5)) / count, { maxSide }).promise.then(frame => {
      if (!alive || !frame || !ctx) return;
      const scale = Math.max(step / frame.width, canvas.height / frame.height);
      ctx.save();
      ctx.beginPath();
      ctx.rect(index * step, 0, step + 1, canvas.height);
      ctx.clip();
      ctx.drawImage(frame, index * step + (step - frame.width * scale) / 2, (canvas.height - frame.height * scale) / 2, frame.width * scale, frame.height * scale);
      ctx.restore();
    });
  });
  return () => { alive = false; };
}

// What a file says about itself, read from its first bytes: pixel size and length. Rejects
// when the browser cannot play it.
export async function readVideoFile(file) {
  const url = URL.createObjectURL(file);
  const video = createVideo(url);
  try {
    await once(video, ['loadedmetadata', 'error'], 20000);
    if (!video.videoWidth || !Number.isFinite(video.duration)) throw new Error('video');
    return { url, width: video.videoWidth, height: video.videoHeight, duration: Math.round(video.duration * 1000) / 1000 };
  } catch {
    URL.revokeObjectURL(url);
    throw Object.assign(new Error('Unreadable video'), { code: 'video_unreadable' });
  } finally {
    video.removeAttribute('src');
    video.load();
  }
}
